import React, {
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { matchPath } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import Loader from "../Loader/Loader";
import { ApiContext } from "../../context/MyContext";
import { hasRouteAccess } from "../../utils/accessControl";
import {
  isInsideFlow,
  matchedPatternInFlow,
  splitPath,
} from "./embeddableFlows";
import { EmbeddedRouteProvider, paramsForPath } from "./embeddedRoute";

/**
 * Renders a route-level feature flow inline, without navigating.
 *
 * The editors this hosts (`AssignDietPlanPage`, `WorkoutPlanPage`, and the
 * template pages they reach) are ~1000 line route pages that take their input
 * from `location.state` and move between steps by navigating. Rather than
 * refactor them into prop-driven components, this keeps their location in
 * state and publishes it through `EmbeddedRouteProvider`; the pages read it via
 * the shim hooks in embeddedRoute.jsx, so every self-navigation works as it
 * does today.
 *
 * There is deliberately no nested `MemoryRouter`: React Router v6 throws
 * "You cannot render a <Router> inside another <Router>", so the synthetic
 * context is the only way to do this without a router of our own.
 *
 * Consequences worth knowing:
 *  - The browser Back button does not step through the flow, because these hops
 *    are not in the address bar. The header's back button is the way out.
 *  - A reload does not restore an open flow; the panel reopens on its tab.
 *  - `PrivateRoute` is skipped: the host is already behind it.
 *
 * @param {Object} props
 * @param {Object} props.flow - Entry from EMBEDDABLE_FLOWS.
 * @param {string} props.to - Destination that opened the flow.
 * @param {Object} [props.state] - `navigate` state for the opening step.
 * @param {(exit: {pathname: string|null, search?: string, state: any,
 *   viaBackButton?: boolean, chunkFailed?: boolean}) => void} props.onExit
 * @param {string} [props.backLabel='Back']
 */
export function EmbeddedFlow({ flow, to, state, onExit, backLabel = "Back" }) {
  const [Provider, setProvider] = useState(null);
  const [location, setLocation] = useState(() => {
    const { pathname, search } = splitPath(to);
    return { pathname, search, state: state ?? null };
  });

  // onExit unmounts this subtree; guard so a second call during teardown can't
  // land on an already-closed panel.
  const exitedRef = useRef(false);
  const handleExit = useCallback(
    (exit) => {
      if (exitedRef.current) return;
      exitedRef.current = true;
      onExit(exit);
    },
    [onExit]
  );

  // Embedding skips PrivateRoute, so apply the same route permission it would
  // have. A denied page hands back to the real router, which runs the app's
  // own redirect rather than silently rendering something out of reach.
  const { employeeData, departmentsWithRoles } = useContext(ApiContext);
  const isPermitted = useCallback(
    (pathname) => {
      const pattern = matchedPatternInFlow(flow, pathname);
      if (!pattern) return false;
      // Matches AppSidebar's convention: before employee data resolves there
      // is nothing to check against, so don't pre-emptively deny.
      if (!employeeData?.data) return true;
      return hasRouteAccess(pattern, employeeData, departmentsWithRoles);
    },
    [flow, employeeData, departmentsWithRoles]
  );

  // The shimmed navigate: stay inside the flow, or hand back to the real
  // router. Note this replaces <Routes> — matching happens below.
  const navigate = useCallback(
    (target, options) => {
      const { pathname, search } = splitPath(target);
      if (isInsideFlow(flow, pathname) && isPermitted(pathname)) {
        setLocation({ pathname, search, state: options?.state ?? null });
        return;
      }
      handleExit({ pathname, search, state: options?.state ?? null });
    },
    [flow, handleExit, isPermitted]
  );

  // The opening step gets the same check as every later hop.
  useEffect(() => {
    if (!isPermitted(location.pathname)) {
      handleExit({
        pathname: location.pathname,
        search: location.search,
        state: location.state,
      });
    }
  }, [isPermitted, location, handleExit]);

  useEffect(() => {
    let active = true;
    flow
      .loadProvider()
      .then((Loaded) => {
        if (active) setProvider(() => Loaded);
      })
      .catch(() => {
        // The chunk failed to load; a real page navigation beats an empty pane.
        if (active) {
          handleExit({ pathname: to, state, chunkFailed: true });
        }
      });
    return () => {
      active = false;
    };
  }, [flow, to, state, handleExit]);

  const match = flow.routes.find((route) =>
    matchPath(route.path, location.pathname)
  );
  const params = paramsForPath(flow.routes, location.pathname);
  const Page = match?.element;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-shrink-0 items-center gap-2 border-b border-gray-200 bg-white px-3 py-2">
        <button
          type="button"
          onClick={() =>
            handleExit({ pathname: null, state: null, viaBackButton: true })
          }
          className="flex items-center gap-1 rounded-lg border border-gray-200 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
        >
          <ChevronLeft className="h-4 w-4" />
          {backLabel}
        </button>
        <span className="truncate text-sm font-semibold text-gray-900">
          {flow.title}
        </span>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {Provider && Page ? (
          <EmbeddedRouteProvider
            location={location}
            navigate={navigate}
            params={params}
          >
            <Suspense fallback={<Loader />}>
              <Provider>
                <Page />
              </Provider>
            </Suspense>
          </EmbeddedRouteProvider>
        ) : (
          <Loader />
        )}
      </div>
    </div>
  );
}

export default EmbeddedFlow;
